import { Box, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getRetention } from 'API/engagement';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { CANCEL_REASONS } from 'types/engagement.types';
import { CohortHeatmap, HBarList } from '../../shared/charts';
import { fmtEuroCents, fmtInt, fmtPct, metricStatusLabel } from '../../shared/format';
import { MetricText } from '../../shared/metric';
import { KpiRow, KpiTile, QueryState, RiskPill, SectionCard } from '../../shared/ui';

const RetentionTab: React.FC = () => {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const q = useQuery({ queryKey: ['engagement', 'retention'], queryFn: getRetention });
  const r = q.data;
  const atRiskMrr = (r?.atRisk ?? []).filter((row) => row.risk === 'at_risk').reduce((a, row) => a + (row.mrrCents ?? 0), 0);
  const churnRows = r
    ? [...CANCEL_REASONS, ...Object.keys(r.churnReasons ?? {}).filter((k) => !CANCEL_REASONS.includes(k as never))].map((reason) => ({
        label: t(`engCancelReason_${reason}`, { defaultValue: reason }),
        value: r.churnReasons?.[reason] ?? 0,
      }))
    : [];
  const churnTotal = churnRows.reduce((a, b) => a + b.value, 0);

  return (
    <QueryState isLoading={q.isLoading} error={q.error}>
      {r ? (
        <Stack gap={2.5}>
          <KpiRow>
            <KpiTile label={t('engKpiPaying')} value={<MetricText value={r.payingTenants} format={fmtInt(lng)} variant={'kpi'} />} />
            <KpiTile label={t('engKpiMrr')} value={<MetricText value={r.mrrCents} format={fmtEuroCents(lng)} variant={'kpi'} />} sub={t('engKpiMrrSub')} />
            <KpiTile label={t('engKpiChurn')} value={<MetricText value={r.churnRateMonth} format={fmtPct(lng, 1)} variant={'kpi'} />} />
            <KpiTile label={t('engKpiNrr')} value={<MetricText value={r.nrr} format={fmtPct(lng)} variant={'kpi'} />} />
            <KpiTile
              label={t('engKpiAtRisk')}
              value={fmtInt(lng)((r.atRisk ?? []).filter((row) => row.risk === 'at_risk').length)}
              sub={t('engKpiAtRiskSub', { mrr: fmtEuroCents(lng)(atRiskMrr) })}
              tone={'warn'}
            />
          </KpiRow>

          <SectionCard title={t('engAtRiskTitle')}>
            {r.atRiskStatus && r.atRiskStatus !== 'ok' ? (
              <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }} data-testid={'at-risk-status'}>
                {t('engAtRiskPartial', { status: metricStatusLabel(r.atRiskStatus, t) })}
              </Typography>
            ) : null}
            {(r.atRisk ?? []).length === 0 ? (
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{t('engAtRiskEmpty')}</Typography>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size={'small'}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('engColTenant')}</TableCell>
                      <TableCell align={'right'}>{t('engColMrr')}</TableCell>
                      <TableCell>{t('engColRisk')}</TableCell>
                      <TableCell>{t('engColReasons')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {r.atRisk.map((row) => (
                      <TableRow key={row.tenantId}>
                        <TableCell sx={{ fontWeight: 600 }}>{row.name}</TableCell>
                        <TableCell align={'right'}>{typeof row.mrrCents === 'number' ? fmtEuroCents(lng)(row.mrrCents) : '—'}</TableCell>
                        <TableCell>
                          <RiskPill risk={row.risk} />
                        </TableCell>
                        <TableCell>{(row.reasons ?? []).join(' · ') || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </SectionCard>

          <Stack direction={{ xs: 'column', lg: 'row' }} gap={2}>
            <Box sx={{ flex: 3, minWidth: 0 }}>
              <SectionCard title={t('engCohortsTitle')}>
                {(r.cohorts ?? []).length ? (
                  <CohortHeatmap cohorts={r.cohorts} format={fmtPct(lng)} />
                ) : (
                  <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{t('engCohortsEmpty')}</Typography>
                )}
                <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1 }}>{t('engCohortsNote')}</Typography>
              </SectionCard>
            </Box>
            <Box sx={{ flex: 2, minWidth: 0 }}>
              <SectionCard title={t('engReactivationTitle')}>
                <Stack gap={1}>
                  {Object.entries(r.reactivation ?? {}).map(([k, v]) => (
                    <Stack key={k} direction={'row'} justifyContent={'space-between'}>
                      <Typography sx={{ fontSize: 13 }}>{t(`engReactivation_${k}`, { defaultValue: k })}</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                        {fmtInt(lng)(v)}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </SectionCard>
              <Box sx={{ mt: 2 }}>
                <SectionCard title={t('engChurnReasonsTitle')}>
                  {churnTotal === 0 ? (
                    <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{t('engChurnReasonsEmpty')}</Typography>
                  ) : (
                    <HBarList rows={churnRows} color={'#FF0A71'} />
                  )}
                </SectionCard>
              </Box>
            </Box>
          </Stack>
        </Stack>
      ) : null}
    </QueryState>
  );
};

export default RetentionTab;
